import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import { api, apiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import {
  SealCheckIcon,
  LightningIcon,
  CrownIcon,
  ClockCountdownIcon,
  ConfettiIcon,
  SparkleIcon,
  TrophyIcon,
  PercentIcon,
  BroadcastIcon,
  ArrowRightIcon,
  CurrencyDollarIcon,
} from "@phosphor-icons/react";

const FOUNDER_PERKS = [
  {
    icon: PercentIcon,
    title: "0% platform fees for 14 days",
    body: "Your 14-day fee-free window starts the moment your application is approved. Every dollar your buyers pay lands in your payout.",
  },
  {
    icon: CrownIcon,
    title: "Founder Seller badge for life",
    body: "A permanent, verified OG mark next to your name. Buyers see it. Algorithms reward it.",
  },
  {
    icon: TrophyIcon,
    title: "Featured on launch day",
    body: "Every Founder gets rotating priority placement on the home grid for the first 14 days.",
  },
  {
    icon: LightningIcon,
    title: "Priority ID verification",
    body: "Founder applications skip the queue. Most are approved within the same business day.",
  },
  {
    icon: BroadcastIcon,
    title: "Unlimited lots per stream",
    body: "Drop as many items into a single live show as you can sell — no tiered caps, no upgrade ladder.",
  },
  {
    icon: SparkleIcon,
    title: "Closed merchant Slack",
    body: "Direct line to the team + private community of the first 250 operators on the platform.",
  },
];

const CATEGORY_CHOICES = [
  "Sneakers",
  "Fragrances & Perfume",
  "Luxury Bags & Accessories",
  "Storage Unit Finds",
  "Trading Card Games",
  "Electronics",
  "Funko",
  "Rocks & Crystals",
  "Sports Cards",
  "Plants",
  "Women's Apparel",
  "Jewelry",
];

export default function ApplyToSell() {
  const [stats, setStats] = useState({
    seats_total: 250,
    seats_claimed: 0,
    spots_left: 250,
    pending: 0,
    closed: false,
  });
  const [loading, setLoading] = useState(true);
  const [submitted, setSubmitted] = useState(false);
  const [form, setForm] = useState({
    brand_name: "",
    contact_name: "",
    email: "",
    phone: "",
    segment: "retail",
    categories: [],
    social_handle: "",
    est_monthly_gmv_usd: "",
    pitch: "",
  });
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    let cancelled = false;
    api
      .get("/seller-applications/stats")
      .then((r) => {
        if (!cancelled) setStats(r.data);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    // Real-time push: every time admin approves/rejects an application
    // OR a new application is submitted, the server broadcasts an
    // updated stats payload here. The countdown ticks live.
    let ws = null;
    try {
      const base = process.env.REACT_APP_BACKEND_URL || "";
      const url = new URL(base);
      url.protocol = url.protocol === "https:" ? "wss:" : "ws:";
      url.pathname = "/api/ws/founder-seats";
      ws = new WebSocket(url.toString());
      ws.onmessage = (ev) => {
        try {
          const d = JSON.parse(ev.data);
          if (d.type === "founder_seats.update" && d.stats) {
            setStats(d.stats);
          }
        } catch {
          /* non-JSON ping */
        }
      };
    } catch {
      /* WS unavailable — initial fetch above still gives a static count */
    }
    return () => {
      cancelled = true;
      if (ws) try { ws.close(); } catch { /* noop */ }
    };
  }, []);

  const toggleCategory = (cat) => {
    setForm((f) => {
      const has = f.categories.includes(cat);
      const next = has
        ? f.categories.filter((c) => c !== cat)
        : f.categories.length >= 8
        ? f.categories
        : [...f.categories, cat];
      return { ...f, categories: next };
    });
  };

  const submit = async (e) => {
    e.preventDefault();
    if (form.categories.length === 0) {
      toast.error("Pick at least one category");
      return;
    }
    if (form.pitch.trim().length < 20) {
      toast.error("Your pitch should be at least 20 characters");
      return;
    }
    setBusy(true);
    try {
      const payload = {
        ...form,
        est_monthly_gmv_usd:
          form.est_monthly_gmv_usd === "" ? null : Number(form.est_monthly_gmv_usd),
      };
      const r = await api.post("/seller-applications", payload);
      setStats(r.data.stats);
      setSubmitted(true);
      toast.success("Application in — you'll hear back within 24h.");
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setBusy(false);
    }
  };

  const percentTaken = Math.min(
    100,
    Math.round(((stats.seats_claimed + stats.pending) / stats.seats_total) * 100)
  );
  const runningOut = stats.spots_left <= 40;

  return (
    <div className="min-h-screen bg-[#F6F4EE]" data-testid="apply-to-sell-page">
      <Navbar />

      {/* Tension strip */}
      <div className="bg-zinc-900 text-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-8 py-3 flex items-center gap-3 text-xs font-mono tracking-wider flex-wrap">
          <ClockCountdownIcon size={14} className="text-amber-400 animate-pulse" />
          <span className="text-amber-400 font-bold">FOUNDER SELLER ROLLOUT</span>
          <span className="text-white/60">
            Closes when the 250<sup>th</sup> seat is claimed — no extensions.
          </span>
          <span className="ml-auto">
            <span className="text-white/60">SEATS LEFT</span>{" "}
            <span className="font-bold text-amber-400" data-testid="tension-seats-left">
              {loading ? "—" : stats.spots_left}
            </span>
            <span className="text-white/30"> / {stats.seats_total}</span>
          </span>
        </div>
      </div>

      {/* Hero */}
      <section className="relative overflow-hidden">
        <div className="absolute inset-0 pointer-events-none">
          <div className="absolute -top-32 -left-32 h-96 w-96 rounded-full bg-[#1D78AB]/20 blur-3xl" />
          <div className="absolute -bottom-40 right-0 h-96 w-96 rounded-full bg-amber-400/20 blur-3xl" />
        </div>
        <div className="relative max-w-7xl mx-auto px-6 lg:px-8 pt-14 pb-10 lg:grid lg:grid-cols-12 lg:gap-10">
          <div className="lg:col-span-7">
            <div className="pill bg-amber-400 text-zinc-900 mb-5">
              <SparkleIcon size={11} weight="fill" /> FOUNDER SELLER APPLICATIONS — OPEN
            </div>
            <h1 className="font-display font-black text-5xl sm:text-6xl lg:text-7xl tracking-tighter leading-[0.95]">
              Be one of the <span className="text-[#1D78AB]">first 250</span>
              <br />
              sellers on Bidorah.
            </h1>
            <p className="mt-6 text-lg text-zinc-700 max-w-2xl leading-relaxed">
              We're hand-picking the operators who shape what live auctions look like in 2026.
              Founders get the lowest fees we will ever offer, a permanent badge, featured
              placement, and the playbook to rebuild what other platforms broke.
            </p>

            {/* Scarcity bar */}
            <div
              className="mt-10 bg-white border border-zinc-200 rounded-2xl p-5 shadow-xl shadow-zinc-900/5"
              data-testid="scarcity-panel"
            >
              <div className="flex items-center justify-between mb-2">
                <div className="text-xs font-semibold text-zinc-500">Founder seats</div>
                <div className="font-mono text-xs text-zinc-500">
                  {stats.pending > 0
                    ? `${stats.pending} applications in review`
                    : "Live roster"}
                </div>
              </div>
              <div className="flex items-baseline gap-3 mb-3">
                <div className="font-display font-black text-4xl tracking-tighter">
                  {stats.seats_claimed}
                </div>
                <div className="text-zinc-400 font-display text-2xl">/</div>
                <div className="font-display font-bold text-2xl text-zinc-600">
                  {stats.seats_total}
                </div>
                <div className="ml-auto text-sm">
                  <span
                    className={`font-bold ${runningOut ? "text-amber-600" : "text-[#1D78AB]"}`}
                    data-testid="spots-left-large"
                  >
                    {stats.spots_left} left
                  </span>
                </div>
              </div>
              <div className="h-2.5 bg-zinc-100 rounded-full overflow-hidden">
                <div
                  className="h-full bg-gradient-to-r from-[#1D78AB] via-[#2190CC] to-amber-400 transition-all duration-700"
                  style={{ width: `${percentTaken}%` }}
                />
              </div>
              <p
                className={`mt-3 text-sm ${
                  stats.closed
                    ? "text-red-600 font-semibold"
                    : runningOut
                    ? "text-amber-700 font-semibold"
                    : "text-zinc-600"
                }`}
              >
                {stats.closed ? (
                  "Every Founder seat has been claimed. Applications are closed."
                ) : runningOut ? (
                  <>
                    <span>Only {stats.spots_left} Founder seats remain.</span>{" "}
                    Once they're gone, Bidorah moves to a curated referral-only onboarding.
                  </>
                ) : (
                  <>
                    After the 250<sup>th</sup> seat, fees return to standard rates and the
                    Founder badge disappears forever. There will not be a second cohort.
                  </>
                )}
              </p>
            </div>

            <a
              href="#apply-form"
              className="mt-7 inline-flex items-center gap-2 font-semibold text-[#1D78AB] hover:underline"
              data-testid="hero-apply-jump"
            >
              Apply now — takes 3 minutes <ArrowRightIcon size={16} />
            </a>
            <Link
              to="/why-bidorah"
              className="mt-3 ml-0 sm:ml-6 inline-flex items-center gap-2 font-semibold text-zinc-600 hover:text-[#1D78AB]"
              data-testid="apply-why-bidorah-link"
            >
              First, see why sellers are switching <span className="text-base">→</span>
            </Link>
          </div>

          {/* Perks rail */}
          <div className="lg:col-span-5 mt-12 lg:mt-0">
            <div className="sticky top-24 space-y-3">
              <p className="text-xs font-semibold text-zinc-500 mb-1">What Founders get</p>
              <div className="grid grid-cols-1 gap-3">
                {FOUNDER_PERKS.map(({ icon: Icon, title, body }) => (
                  <div
                    key={title}
                    className="bg-white border border-zinc-200 rounded-2xl p-4 flex gap-3 hover:border-[#1D78AB] transition-colors"
                  >
                    <div className="h-9 w-9 rounded-xl bg-[#1D78AB]/10 text-[#1D78AB] grid place-items-center flex-shrink-0">
                      <Icon size={18} weight="bold" />
                    </div>
                    <div>
                      <p className="font-display font-bold text-sm leading-tight">{title}</p>
                      <p className="text-xs text-zinc-600 mt-1 leading-relaxed">{body}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Why pick us bar */}
      <section className="bg-white border-y border-zinc-200">
        <div className="max-w-7xl mx-auto px-6 lg:px-8 py-14 grid md:grid-cols-3 gap-10">
          <div>
            <div className="pill pill-dark mb-3">NON-NEGOTIABLE</div>
            <h3 className="font-display font-black text-2xl tracking-tight">
              Zero wholesale-retail crossover.
            </h3>
            <p className="text-sm text-zinc-600 mt-3">
              Platforms lets wholesalers dump retail. We built the wall into the schema. Retail buyers
              cannot bid on wholesale lots. Wholesale buyers cannot buy retail. Enforced on every
              write, not at checkout.
            </p>
          </div>
          <div>
            <div className="pill pill-dark mb-3">INSTANT PAYOUTS</div>
            <h3 className="font-display font-black text-2xl tracking-tight">
              Hit 500 sales, payouts go instant.
            </h3>
            <p className="text-sm text-zinc-600 mt-3">
              Your Trust Tier publicly graduates from NEW → RISING → TRUSTED → INSTANT. INSTANT
              sellers funds release the moment the buyer pays. No 7-day holds.
            </p>
          </div>
          <div>
            <div className="pill pill-dark mb-3">LIVE, NOT RECORDED</div>
            <h3 className="font-display font-black text-2xl tracking-tight">
              Real-time bidding, real-time chat.
            </h3>
            <p className="text-sm text-zinc-600 mt-3">
              Viewer count, presence joins, and outbid alerts all fire live. No fake viewer counts.
              No delayed auctions.
            </p>
          </div>
        </div>
      </section>

      {/* Keep More / Fee Transparency */}
      <section
        className="relative overflow-hidden border-b border-zinc-200 bg-zinc-900 text-white"
        data-testid="keep-more-section"
      >
        <div className="absolute inset-0 pointer-events-none">
          <div className="absolute -top-32 -right-32 h-96 w-96 rounded-full bg-[#1D78AB]/30 blur-3xl" />
          <div className="absolute -bottom-40 -left-20 h-96 w-96 rounded-full bg-amber-400/20 blur-3xl" />
        </div>
        <div className="relative max-w-7xl mx-auto px-6 lg:px-8 py-20 lg:grid lg:grid-cols-12 lg:gap-12">
          <div className="lg:col-span-5">
            <div className="pill bg-amber-400 text-zinc-900 mb-5">
              <PercentIcon size={11} weight="fill" /> KEEP MORE OF WHAT YOU EARN
            </div>
            <h2 className="font-display font-black text-4xl lg:text-5xl tracking-tighter leading-[0.98]">
              Sellers should keep more of what they earn.
            </h2>
            <p className="text-lg text-white/70 mt-5 leading-relaxed max-w-md">
              A simple, transparent fee structure designed to put you first. What you see is what
              you pay — no drip-pricing, no surprise deductions.
            </p>
            <div className="mt-8 flex flex-wrap gap-2 font-display font-black tracking-tighter">
              <span className="px-4 py-2 rounded-full bg-white text-zinc-900 text-sm">
                Sell smarter.
              </span>
              <span className="px-4 py-2 rounded-full bg-amber-400 text-zinc-900 text-sm">
                Keep more.
              </span>
              <span className="px-4 py-2 rounded-full bg-[#1D78AB] text-white text-sm">
                Grow faster.
              </span>
            </div>
          </div>

          <div className="lg:col-span-7 mt-12 lg:mt-0 grid sm:grid-cols-2 gap-4" data-testid="fee-cards">
            <div className="rounded-3xl bg-white/5 backdrop-blur-md border border-white/10 p-6 hover:border-amber-400/60 transition-colors">
              <div className="flex items-start justify-between mb-4">
                <div className="h-10 w-10 rounded-xl bg-amber-400/15 text-amber-300 grid place-items-center">
                  <PercentIcon size={20} weight="bold" />
                </div>
                <span className="overline text-white/40">COMMISSION</span>
              </div>
              <p className="font-display font-black text-5xl tracking-tighter">
                5<span className="text-amber-400">%</span>
              </p>
              <p className="text-sm font-semibold text-white/90 mt-2">
                Commission on final sale price
              </p>
              <p className="text-xs text-white/60 mt-1 leading-relaxed">
                Significantly less than typical marketplace fees. Applied only when you sell.
              </p>
            </div>

            <div className="rounded-3xl bg-white/5 backdrop-blur-md border border-white/10 p-6 hover:border-[#1D78AB] transition-colors">
              <div className="flex items-start justify-between mb-4">
                <div className="h-10 w-10 rounded-xl bg-[#1D78AB]/20 text-[#4FB3E8] grid place-items-center">
                  <CurrencyDollarIcon size={20} weight="bold" />
                </div>
                <span className="overline text-white/40">PROCESSING</span>
              </div>
              <p className="font-display font-black text-5xl tracking-tighter">
                2.5<span className="text-[#4FB3E8]">%</span>
                <span className="text-white/50 text-3xl"> + $0.25</span>
              </p>
              <p className="text-sm font-semibold text-white/90 mt-2">
                Payment processing per order
              </p>
              <p className="text-xs text-white/60 mt-1 leading-relaxed">
                Competitive card-network rates. Minimal fixed charges on every successful order.
              </p>
            </div>

            <div className="sm:col-span-2 rounded-3xl bg-gradient-to-br from-emerald-500/15 to-emerald-500/5 backdrop-blur-md border border-emerald-400/30 p-6 flex items-start gap-4">
              <div className="h-10 w-10 rounded-xl bg-emerald-400/20 text-emerald-300 grid place-items-center flex-shrink-0">
                <SealCheckIcon size={20} weight="fill" />
              </div>
              <div className="flex-1">
                <p className="font-display font-bold text-lg">No hidden costs.</p>
                <p className="text-sm text-white/70 mt-1 leading-relaxed">
                  Whether you're moving everyday items or high-value collectibles, Bidorah is built
                  to maximize your margins and minimize your costs. What you see is exactly what you
                  pay.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Application form */}
      <section id="apply-form" className="max-w-4xl mx-auto px-6 lg:px-8 py-16">
        {submitted ? (
          <div
            className="bg-white border border-emerald-300 rounded-3xl p-10 text-center shadow-xl"
            data-testid="apply-success-card"
          >
            <ConfettiIcon size={48} className="mx-auto text-emerald-500" weight="fill" />
            <h2 className="font-display font-black text-3xl tracking-tight mt-4">
              Application received.
            </h2>
            <p className="text-zinc-600 mt-3 max-w-lg mx-auto">
              We're reviewing Founder applications in the order they arrive. Expect a decision in
              your inbox (and in-app notification if you register with the same email) within 24
              hours.
            </p>
            <div className="mt-6 text-sm text-zinc-500">
              Seats left: <b className="text-[#1D78AB]">{stats.spots_left}</b> · Keep an eye on your
              email.
            </div>
          </div>
        ) : (
          <>
            <div className="text-center max-w-2xl mx-auto mb-10">
              <h2 className="font-display font-black text-4xl tracking-tighter mt-2">
                Three minutes. No resume. No BS.
              </h2>
              <p className="text-sm text-zinc-600 mt-3">
                Tell us what you sell, how you've been selling it, and why buyers should trust you.
                We reply to every application.
              </p>
            </div>

            <form
              onSubmit={submit}
              className="bg-white border border-zinc-200 rounded-3xl p-6 lg:p-10 shadow-xl shadow-zinc-900/5 space-y-5"
              data-testid="apply-form"
            >
              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <Input
                    value={form.brand_name}
                    onChange={(e) => setForm({ ...form, brand_name: e.target.value })}
                    placeholder="Brand / handle *"
                    className="rounded-full h-11 px-4"
                    required
                    data-testid="apply-brand-name"
                  />
                </div>
                <div>
                  <Input
                    value={form.contact_name}
                    onChange={(e) => setForm({ ...form, contact_name: e.target.value })}
                    placeholder="Your name *"
                    className="rounded-full h-11 px-4"
                    required
                    data-testid="apply-contact-name"
                  />
                </div>
                <div>
                  <Input
                    type="email"
                    value={form.email}
                    onChange={(e) => setForm({ ...form, email: e.target.value })}
                    placeholder="Email *"
                    className="rounded-full h-11 px-4"
                    required
                    data-testid="apply-email"
                  />
                </div>
                <div>
                  <Input
                    value={form.phone}
                    onChange={(e) => setForm({ ...form, phone: e.target.value })}
                    placeholder="Phone"
                    className="rounded-full h-11 px-4"
                    data-testid="apply-phone"
                  />
                </div>
                <div>
                  <Input
                    type="number"
                    min="0"
                    value={form.est_monthly_gmv_usd}
                    onChange={(e) =>
                      setForm({ ...form, est_monthly_gmv_usd: e.target.value })
                    }
                    placeholder="Monthly GMV (USD)"
                    className="rounded-full h-11 px-4"
                    data-testid="apply-gmv"
                  />
                </div>
                <div>
                  <Input
                    value={form.social_handle}
                    onChange={(e) => setForm({ ...form, social_handle: e.target.value })}
                    placeholder="Instagram / TikTok / X handle"
                    className="rounded-full h-11 px-4"
                    data-testid="apply-social"
                  />
                </div>
              </div>

              <div>
                <div className="flex flex-wrap gap-2">
                  {CATEGORY_CHOICES.map((cat) => {
                    const active = form.categories.includes(cat);
                    return (
                      <button
                        key={cat}
                        type="button"
                        onClick={() => toggleCategory(cat)}
                        className={`px-4 py-2 rounded-full text-sm font-medium border transition-all ${
                          active
                            ? "bg-[#1D78AB] text-white border-[#1D78AB] shadow-md shadow-[#1D78AB]/20"
                            : "bg-white text-zinc-700 border-zinc-200 hover:border-[#1D78AB] hover:text-[#1D78AB]"
                        }`}
                        data-testid={`apply-category-${cat.replace(/\s+/g, "-").toLowerCase()}`}
                      >
                        {cat}
                      </button>
                    );
                  })}
                </div>
              </div>

              <div>
                <Textarea
                  value={form.pitch}
                  onChange={(e) => setForm({ ...form, pitch: e.target.value })}
                  placeholder="Why should we pick you? What do you sell, who's buying, and what makes your live show worth watching? 2–4 sentences is perfect."
                  rows={5}
                  className="rounded-2xl px-4 py-3 resize-none"
                  required
                  data-testid="apply-pitch"
                />
                <p className="text-[11px] text-zinc-500 mt-1">
                  {form.pitch.length}/800 · Minimum 20 characters
                </p>
              </div>

              <div className="flex items-center justify-between pt-3 border-t border-zinc-100 flex-wrap gap-3">
                <p className="text-xs text-zinc-500 flex items-center gap-1.5">
                  <SealCheckIcon size={14} className="text-[#1D78AB]" />
                  ID verification happens after approval. No fees until you're live.
                </p>
                <Button
                  type="submit"
                  disabled={busy || stats.closed}
                  className="bg-[#1D78AB] hover:bg-[#165d85] text-white h-12 px-7 rounded-full font-semibold shadow-lg shadow-[#1D78AB]/20 hover:shadow-xl hover:shadow-[#1D78AB]/30 hover:-translate-y-0.5 transition-all"
                  data-testid="apply-submit-btn"
                >
                  {stats.closed
                    ? "Applications closed"
                    : busy
                    ? "Submitting…"
                    : "Apply for a Founder seat"}
                  {!stats.closed && !busy && <ArrowRightIcon size={16} className="ml-2" />}
                </Button>
              </div>
            </form>

            <p className="text-xs text-zinc-500 text-center mt-6 max-w-xl mx-auto">
              By submitting, you confirm you're applying as a registered business. Duplicate,
              reseller, or counterfeit operations are rejected on sight.
            </p>
          </>
        )}
      </section>

      <Footer />
    </div>
  );
}
